import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import DuplicateCourseRegistrationsView from '../../components/reports/DuplicateCourseRegistrationsView';

function getReportParam() {
  const params = new URLSearchParams(window.location.search);
  return params.get('report') || null;
}

// ─── Report catalogue ─────────────────────────────────────────────────────────

const REPORTS = [
  {
    id: 'partial-attendance',
    title: 'Partial Attendance Report',
    category: 'Attendance',
    description:
      'Students who attended at least one class but missed another on a selected date. Filter by course and subject — subject view splits all students into Present and Absent lists.',
    source: 'class_schedules · attendance · registrations · students',
    iconPath: 'M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
    iconBg: 'bg-amber-50',
    iconText: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700',
    externalUrl: '/admin/report/partial-attendance',
  },
  {
    id: 'batch-subject-attendance',
    title: 'Batch-wise Subject Attendance',
    category: 'Attendance',
    description:
      'Subject-level attendance percentages for every ongoing batch in your assigned courses. Shows both ongoing and completed subjects with classes held, total present, and the overall attendance rate per subject.',
    source: 'batch_subjects · class_schedules · attendance · registrations',
    iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
    iconBg: 'bg-green-50',
    iconText: 'text-green-600',
    badge: 'bg-green-100 text-green-700',
  },
  {
    id: 'lecturer-subject-attendance',
    title: 'Lecturer-wise Subject Attendance',
    category: 'Attendance',
    description:
      'Subject-level attendance percentages grouped by lecturer for your assigned courses. Displays each staff member\'s ongoing and completed subject assignments with attendance rates.',
    source: 'batch_subjects · staff · class_schedules · attendance · registrations',
    iconPath: 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z',
    iconBg: 'bg-purple-50',
    iconText: 'text-purple-600',
    badge: 'bg-purple-100 text-purple-700',
  },
  {
    id: 'student-attendance',
    title: 'Student Attendance Report',
    category: 'Attendance',
    description:
      'Each student\'s overall attendance percentage aggregated across every subject in their batch. Filter by batch and set a minimum percentage threshold to focus on at-risk students.',
    source: 'registrations · batch_subjects · class_schedules · attendance · students',
    iconPath: 'M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',
    iconBg: 'bg-blue-50',
    iconText: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700',
  },
  {
    id: 'duplicate-course-registrations',
    title: 'Students by Course (Duplicate Check)',
    category: 'Registration',
    description:
      'All students in your assigned courses, grouped by course name. Flags any student registered in two or more batches under courses that share the same name but have different course IDs.',
    source: 'courses · batches · registrations · students',
    iconPath: 'M8 7V5a2 2 0 012-2h9a2 2 0 012 2v9a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-2M8 7h8v8H8V7z',
    iconBg: 'bg-rose-50',
    iconText: 'text-rose-600',
    badge: 'bg-rose-100 text-rose-700',
    noCourseFilter: true,
  },
];

// ─── Shared react-select styles ───────────────────────────────────────────────

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#6366f1' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(99,102,241,0.25)' : 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    minHeight: '36px',
    '&:hover': { borderColor: '#6366f1' },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#eef2ff' : 'white',
    color: state.isSelected ? 'white' : '#111827',
    whiteSpace: 'nowrap',
  }),
  menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  singleValue: (base) => ({ ...base, color: '#111827', fontSize: '0.875rem' }),
  indicatorSeparator: () => ({ display: 'none' }),
};

function formatCourseOption({ label, scheduleType }) {
  return (
    <div className="flex items-center gap-2">
      <span>{label}</span>
      {scheduleType && (
        <span className="shrink-0 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded-full px-1.5 py-0.5 leading-none">
          {scheduleType}
        </span>
      )}
    </div>
  );
}

// ─── Shared small components ──────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function AttendanceBadge({ pct }) {
  if (pct === null || pct === undefined)
    return <span className="text-xs text-gray-400 italic">No data</span>;
  const color =
    pct >= 80 ? 'bg-emerald-100 text-emerald-700'
    : pct >= 60 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>
      {pct.toFixed(1)}%
    </span>
  );
}

function AttendanceBar({ pct }) {
  if (pct === null || pct === undefined) return null;
  const clamped = Math.min(100, Math.max(0, pct));
  const bar = pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
      <div className={`h-1.5 rounded-full ${bar}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = { ongoing: 'bg-emerald-100 text-emerald-700', completed: 'bg-blue-100 text-blue-700' };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${cfg[status] || 'bg-gray-100 text-gray-500'}`}>
      {status || '—'}
    </span>
  );
}

function PersonAvatar({ name, imagePath }) {
  const [err, setErr] = useState(false);
  const initials = (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  if (!err && imagePath) {
    return (
      <img src={getImageURL(imagePath)} alt={name} onError={() => setErr(true)}
        className="w-7 h-7 rounded-full shrink-0 object-cover border border-gray-200 shadow-sm" />
    );
  }
  return (
    <div className="w-7 h-7 rounded-full shrink-0 border border-gray-200 shadow-sm bg-gradient-to-br from-slate-100 to-blue-100 flex items-center justify-center">
      <span className="font-bold text-blue-700 text-[9px] leading-none">{initials}</span>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
      <svg className="w-12 h-12 mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <p className="text-sm">{message || 'No data available.'}</p>
    </div>
  );
}

// ─── Batch-wise Subject Attendance ────────────────────────────────────────────

function BatchSubjectReport({ courseId }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    if (!courseId) { setData(null); return; }
    setLoading(true);
    setError(null);
    setExpanded(new Set());
    axiosInstance.get('/admin/coordinator-reports/batch-subject-attendance', { params: { courseId } })
      .then((r) => setData(r.data.batches || []))
      .catch(() => setError('Failed to load batch subject attendance.'))
      .finally(() => setLoading(false));
  }, [courseId]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  if (!courseId) return <EmptyState message="Select a course to view this report." />;
  if (loading) return <LoadingSpinner />;
  if (error) return <EmptyState message={error} />;
  if (!data || !data.length) return <EmptyState message="No ongoing batches found for this course." />;

  return (
    <div className="space-y-3">
      {data.map((batch) => {
        const isOpen = expanded.has(batch.batchId);
        const label = batch.whatsappGroupName || batch.batchInternalName || batch.batchName;
        const vals = batch.subjects.map((s) => s.attendancePct).filter((v) => v !== null);
        const avgPct = vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : null;

        return (
          <div key={batch.batchId} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <button type="button" onClick={() => toggle(batch.batchId)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate text-left">{label}</p>
                <p className="text-xs text-gray-400 text-left">{batch.courseShortName || batch.courseName} · {batch.enrolledCount} enrolled</p>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                {avgPct !== null && <AttendanceBadge pct={avgPct} />}
                <div className="flex gap-1.5">
                  {batch.ongoingCount > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{batch.ongoingCount} ongoing</span>
                  )}
                  {batch.completedCount > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">{batch.completedCount} done</span>
                  )}
                </div>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-gray-100 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead>
                    <tr className="bg-gray-50">
                      {['Subject', 'Lecturer', 'Period', 'Status', 'Classes', 'Attendance'].map((h, i) => (
                        <th key={h} className={`px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider ${i >= 4 ? 'text-right' : 'text-left'} ${h === 'Attendance' ? 'min-w-[140px]' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {batch.subjects.map((s) => (
                      <tr key={s.batchSubjectId} className="hover:bg-gray-50/50">
                        <td className="px-4 py-2.5">
                          <p className="text-sm font-medium text-gray-800">{s.subjectName}</p>
                          {s.subjectCode && <p className="text-[10px] text-gray-400">{s.subjectCode}</p>}
                        </td>
                        <td className="px-4 py-2.5">
                          {s.staffName ? (
                            <div className="flex items-center gap-2">
                              <PersonAvatar name={s.staffName} imagePath={s.staffImagePath} />
                              <span className="text-xs text-gray-700">{s.staffName}</span>
                            </div>
                          ) : <span className="text-xs text-gray-400 italic">Unassigned</span>}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(s.startDate)} → {fmtDate(s.endDate)}</td>
                        <td className="px-4 py-2.5"><StatusBadge status={s.status} /></td>
                        <td className="px-4 py-2.5 text-right text-xs text-gray-600">{s.classesHeld} held / {s.enrolledCount} students</td>
                        <td className="px-4 py-2.5 text-right">
                          <AttendanceBadge pct={s.attendancePct} />
                          <AttendanceBar pct={s.attendancePct} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Lecturer-wise Subject Attendance ─────────────────────────────────────────

function LecturerSubjectReport({ courseId }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    if (!courseId) { setData(null); return; }
    setLoading(true);
    setError(null);
    setExpanded(new Set());
    axiosInstance.get('/admin/coordinator-reports/lecturer-subject-attendance', { params: { courseId } })
      .then((r) => setData(r.data.lecturers || []))
      .catch(() => setError('Failed to load lecturer attendance.'))
      .finally(() => setLoading(false));
  }, [courseId]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  if (!courseId) return <EmptyState message="Select a course to view this report." />;
  if (loading) return <LoadingSpinner />;
  if (error) return <EmptyState message={error} />;
  if (!data || !data.length) return <EmptyState message="No lecturer data found for this course." />;

  return (
    <div className="space-y-3">
      {data.map((lec) => {
        const isOpen = expanded.has(lec.staffId);
        const vals = lec.subjects.map((s) => s.attendancePct).filter((v) => v !== null);
        const avgPct = vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : null;

        return (
          <div key={lec.staffId} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <button type="button" onClick={() => toggle(lec.staffId)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <PersonAvatar name={lec.staffName} imagePath={lec.imagePath} />
                <div>
                  <p className="text-sm font-semibold text-gray-900 text-left">{lec.staffName}</p>
                  <p className="text-xs text-gray-400 text-left">{lec.subjects.length} subject{lec.subjects.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                {avgPct !== null && <AttendanceBadge pct={avgPct} />}
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-gray-100 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead>
                    <tr className="bg-gray-50">
                      {['Subject', 'Batch', 'Period', 'Status', 'Attendance'].map((h, i) => (
                        <th key={h} className={`px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider ${i >= 4 ? 'text-right' : 'text-left'} ${h === 'Attendance' ? 'min-w-[140px]' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {lec.subjects.map((s) => (
                      <tr key={s.batchSubjectId} className="hover:bg-gray-50/50">
                        <td className="px-4 py-2.5">
                          <p className="text-sm font-medium text-gray-800">{s.subjectName}</p>
                          {s.subjectCode && <p className="text-[10px] text-gray-400">{s.subjectCode}</p>}
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="text-xs text-gray-700">{s.cohortLabel}</p>
                          <p className="text-[10px] text-gray-400">{s.courseShortName || s.courseName}</p>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(s.startDate)} → {fmtDate(s.endDate)}</td>
                        <td className="px-4 py-2.5"><StatusBadge status={s.status} /></td>
                        <td className="px-4 py-2.5 text-right">
                          <AttendanceBadge pct={s.attendancePct} />
                          <AttendanceBar pct={s.attendancePct} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Student Attendance ───────────────────────────────────────────────────────

function StudentAttendanceReport({ courseId }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [minPct, setMinPct]       = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [search, setSearch]       = useState('');

  const fetchData = (params) => {
    setLoading(true);
    setError(null);
    axiosInstance.get('/admin/coordinator-reports/student-attendance', { params })
      .then((r) => setData(r.data))
      .catch(() => setError('Failed to load student attendance.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!courseId) { setData(null); return; }
    setSelectedBatch(null);
    setMinPct('');
    setSearch('');
    fetchData({ courseId });
  }, [courseId]);

  const handleApply = () => {
    if (!courseId) return;
    const params = { courseId };
    if (selectedBatch) params.batchId = selectedBatch.value;
    if (minPct !== '') params.minPercentage = minPct;
    fetchData(params);
  };

  const filtered = useMemo(() => {
    if (!data?.students) return [];
    const q = search.trim().toLowerCase();
    const list = q
      ? data.students.filter(
          (s) => s.studentName.toLowerCase().includes(q) || (s.registrationNumber || '').toLowerCase().includes(q)
        )
      : [...data.students];
    return list.sort((a, b) => {
      if (a.attendancePct === null && b.attendancePct === null) return 0;
      if (a.attendancePct === null) return 1;
      if (b.attendancePct === null) return -1;
      return b.attendancePct - a.attendancePct;
    });
  }, [data, search]);

  if (!courseId) return <EmptyState message="Select a course to view this report." />;

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 items-end bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
        {data?.batches?.length > 0 && (
          <div className="w-56">
            <label className="block text-[11px] font-medium text-gray-500 mb-1">Batch</label>
            <Select
              options={data.batches.map((b) => ({
                value: b.batchId,
                label: b.whatsappGroupName || b.batchInternalName || b.batchName,
              }))}
              value={selectedBatch}
              onChange={setSelectedBatch}
              placeholder="All batches"
              isClearable
              styles={selectStyles}
            />
          </div>
        )}
        <div>
          <label className="block text-[11px] font-medium text-gray-500 mb-1">Min Attendance %</label>
          <input type="number" min="0" max="100" placeholder="e.g. 75"
            value={minPct} onChange={(e) => setMinPct(e.target.value)}
            className="w-28 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <button type="button" onClick={handleApply} disabled={loading}
          className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
          Apply
        </button>
        <div className="ml-auto">
          <input type="text" placeholder="Search student…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48" />
        </div>
      </div>

      {loading && <LoadingSpinner />}
      {error && !loading && <EmptyState message={error} />}

      {!loading && !error && data && (
        <>
          <p className="text-xs text-gray-400">
            {filtered.length} student{filtered.length !== 1 ? 's' : ''}
            {data.minPercentage !== null ? ` · min ${data.minPercentage}%` : ''}
          </p>
          {filtered.length === 0 ? (
            <EmptyState message="No students match the current filters." />
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-gray-50">
                    {['Student', 'Reg. No.', 'Batch', 'Classes', 'Attendance'].map((h, i) => (
                      <th key={h} className={`px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider ${i >= 3 ? 'text-right' : 'text-left'} ${h === 'Attendance' ? 'min-w-[140px]' : ''}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((s) => (
                    <tr key={`${s.studentId}-${s.registrationNumber}`} className="hover:bg-gray-50/50">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <PersonAvatar name={s.studentName} imagePath={s.imagePath} />
                          <span className="text-sm font-medium text-gray-800">{s.studentName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{s.registrationNumber || '—'}</td>
                      <td className="px-4 py-2.5">
                        <p className="text-xs text-gray-700">{s.whatsappGroupName || s.batchInternalName || s.batchName}</p>
                        <p className="text-[10px] text-gray-400">{s.courseName}</p>
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs text-gray-600">{s.presentCount} / {s.totalClasses}</td>
                      <td className="px-4 py-2.5 text-right">
                        <AttendanceBadge pct={s.attendancePct} />
                        <AttendanceBar pct={s.attendancePct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Report card ──────────────────────────────────────────────────────────────

function ReportCard({ report }) {
  const handleView = () => {
    const url = report.externalUrl
      ? report.externalUrl
      : `/admin/coordinator-reports?report=${report.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col shadow-sm transition-shadow duration-200 hover:shadow-md">
      <div className="p-5 flex-1">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-9 h-9 ${report.iconBg} rounded-lg flex items-center justify-center mt-0.5`}>
            <svg className={`w-5 h-5 ${report.iconText}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d={report.iconPath} />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${report.badge}`}>
              {report.category}
            </span>
            <h3 className="text-sm font-semibold text-gray-900 leading-snug mt-1.5">{report.title}</h3>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500 leading-relaxed">{report.description}</p>
      </div>

      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between rounded-b-xl">
        <span className="text-[10px] text-gray-300 truncate max-w-[55%]" title={report.source}>
          {report.source}
        </span>
        <button
          type="button"
          onClick={handleView}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 transition-colors cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7S1.274 16.057 0 12z" />
          </svg>
          View Report
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CoordinatorReports() {
  const [courses, setCourses]               = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Detect ?report= param — when present we're in single-report view (new tab)
  const reportParam = getReportParam();
  const reportDef   = REPORTS.find((r) => r.id === reportParam) || null;

  useEffect(() => {
    axiosInstance.get('/admin/coordinator-reports/courses')
      .then((r) => {
        const list = r.data.courses || [];
        setCourses(list);
        if (list.length === 1) {
          setSelectedCourse({ value: list[0].courseId, label: list[0].fullName });
        }
      })
      .catch(() => {})
      .finally(() => setLoadingCourses(false));
  }, []);

  const courseOptions = courses.map((c) => ({ value: c.courseId, label: c.fullName, scheduleType: c.scheduleType || '' }));
  const numCourseId = selectedCourse ? Number(selectedCourse.value) : null;

  // ── Single-report view (opened from "View Report" in a new tab) ──────────────
  if (reportDef) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-gray-50 px-6 py-8 space-y-6">
          {/* Header */}
          <div className="border-b border-gray-200 pb-5">
            <div className="flex items-center gap-3 mb-1">
              <div className={`w-8 h-8 ${reportDef.iconBg} rounded-lg flex items-center justify-center`}>
                <svg className={`w-4 h-4 ${reportDef.iconText}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={reportDef.iconPath} />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">{reportDef.title}</h1>
                <p className="text-xs text-gray-400 mt-0.5">Coordinator Reports</p>
              </div>
            </div>
          </div>

          {/* Course selector */}
          {!reportDef.noCourseFilter && (
            <div className="flex items-center gap-4 bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
              <label className="text-sm font-medium text-gray-700 shrink-0">Select Course</label>
              {loadingCourses ? (
                <div className="h-4 w-48 bg-gray-100 rounded animate-pulse" />
              ) : courses.length === 0 ? (
                <span className="text-sm text-gray-400 italic">No courses assigned to you.</span>
              ) : (
                <div className="flex-1 max-w-sm">
                  <Select
                    options={courseOptions}
                    value={selectedCourse}
                    onChange={setSelectedCourse}
                    placeholder="— Choose a course —"
                    isClearable
                    formatOptionLabel={formatCourseOption}
                    styles={selectStyles}
                  />
                </div>
              )}
            </div>
          )}

          {/* Report content */}
          {reportDef.id === 'batch-subject-attendance'    && <BatchSubjectReport     courseId={numCourseId} />}
          {reportDef.id === 'lecturer-subject-attendance' && <LecturerSubjectReport  courseId={numCourseId} />}
          {reportDef.id === 'student-attendance'          && <StudentAttendanceReport courseId={numCourseId} />}
          {reportDef.id === 'duplicate-course-registrations' && <DuplicateCourseRegistrationsView />}
        </div>
      </DashboardLayout>
    );
  }

  // ── Card selection landing page ───────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 px-6 py-8">

        {/* Header */}
        <div className="mb-8 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414A1 1 0 0 1 19 9.414V19a2 2 0 0 1-2 2z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Coordinator Reports</h1>
          </div>
          <p className="text-sm text-gray-500 ml-11">
            Click View Report on any card to open it in a new tab and select your course.
          </p>
        </div>

        {/* Report cards */}
        <div>
          <h2 className="text-sm font-bold text-gray-800 mb-1">Attendance Reports</h2>
          <p className="text-xs text-gray-400 mb-4">Course-level attendance visibility for your assigned batches</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REPORTS.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
